import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import persistStorage from "redux-persist/lib/storage";

import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { clearSelection } from "@redux/slices/tenantSlice";
import { clearNetworkEventsActiveCount } from "@redux/slices/networkAlertsSlice";
import { getLogoutRedirectUri } from "@axios-config/authRedirect";
import { useTheme } from "../../../../../theme/components/ThemeProvider";

import { ReactComponent as SunIcon } from "@assets/sun.svg";
import { ReactComponent as MoonIcon } from "@assets/moon.svg";
import { ReactComponent as ProfileIcon } from "@assets/profile-circle.svg";
import { ReactComponent as ChevronDownSvg } from "@assets/Icons/IconChevron-Down.svg";
import { ReactComponent as IconDisplay } from "@assets/Icons/IconDisplay.svg";
import { ReactComponent as IconSettings } from "@assets/Icons/IconSettings.svg";
import { ReactComponent as IconLogout } from "@assets/Icons/IconLogout.svg";
import MobileBottomSheet from "./MobileBottomSheet";
import PanelSpinner from "./PanelSpinner";
import { usePanelReveal } from "../../hooks/usePanelReveal";
import {
  CHEVRON_ICON,
  CHROME_LOGO,
  CHROME_TRIGGER,
  FOCUS_RING,
  PANEL_OPEN_DOWN,
  RAIL_WIDTH_EXPANDED,
  ROW_GAP,
  ROW_ICON,
  ROW_ICON_STANDALONE,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  TRANSITION_COLORS,
  TRANSITION_TRANSFORM,
} from "../../sideMenuDensity";
import { getAccountScopeLine } from "../../sideMenuVisibility";

export type SideMenuAccountCardProps = {
  logoSrc: string;
  onLogoError: () => void;
  productOptions: string[];
  selectedProduct: string;
  onSelectProduct: (value: string) => void;
  /** Icon-only trigger for the collapsed sidebar. */
  compact?: boolean;
  /** Mobile drawer variant: full-width trigger in the drawer row, and the card
   *  content opens as a bottom sheet (portal) instead of an absolute dropdown. */
  mobile?: boolean;
  /**
   * Whether the Admin center row renders at all — `canAccessAdminCenter`
   * upstream (GLOBAL_ADMIN or the email whitelist), decided there so the rule
   * is stated once (CTX-583).
   */
  showAdminCenterRow: boolean;
  /** Opens the Admin center panel (reuses SideMenu's handler). */
  onOpenAdminCenter: (e: React.MouseEvent) => void;
};

/**
 * Inline light/dark segmented control (the "Display" row) styled as a switch:
 * a gray track with an elevated white "thumb" on the active option. Icons use
 * `[&_path]:fill-current` so the active one picks up the brand (teal) color.
 */
function DisplayToggle() {
  const { theme, setTheme } = useTheme();
  const cell = (active: boolean) =>
    `flex h-6 w-8 items-center justify-center rounded ${TRANSITION_COLORS} ${FOCUS_RING} ${
      active
        ? "bg-semantic-bg-primary text-semantic-text-brand shadow-sm"
        : "text-semantic-text-secondary hover:text-semantic-text-primary"
    }`;
  return (
    <div className="flex items-center gap-1 rounded-lg bg-semantic-bg-secondary p-1">
      <button
        type="button"
        aria-label="Light theme"
        onClick={() => setTheme("theme-light")}
        className={cell(theme === "theme-light")}
      >
        <SunIcon className={`${ROW_ICON_STANDALONE} [&_path]:fill-current`} />
      </button>
      <button
        type="button"
        aria-label="Dark theme"
        onClick={() => setTheme("theme-dark")}
        className={cell(theme === "theme-dark")}
      >
        <MoonIcon className={`${ROW_ICON_STANDALONE} [&_path]:fill-current`} />
      </button>
    </div>
  );
}

/**
 * Holds the product list behind a centred spinner during the panel's open reveal.
 *
 * The list stays rendered underneath, `invisible`: `visibility: hidden` keeps its
 * box, so the panel is exactly as tall while the spinner shows as it is once the
 * list appears. Reserving that height any other way would mean hard-coding a row
 * height here. It also drops the rows from the tab order, and `aria-hidden` keeps
 * them out of the accessibility tree until they are real.
 *
 * The tenant selector needs no equivalent — its columns are a fixed height, so
 * its spinner can simply take the list's place.
 */
function PanelRevealOverlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      <div className="invisible" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Silent: nothing is loading, so nothing is announced. */}
        <PanelSpinner className="h-5 w-5" decorative />
      </div>
    </div>
  );
}

/**
 * Logo dropdown "account card". The trigger shows the logo + the selected
 * product name; opening it reveals the product list (radio-style), the user
 * identity, the Display (theme) toggle, Settings and Log out, plus the CTX brand
 * logo at the foot. On desktop the content is an absolute dropdown; on mobile
 * (`mobile`) it opens as a full-width bottom sheet. Partner/customer selection
 * lives in the separate tenant card (SideMenuTenantCard).
 */
export default function SideMenuAccountCard({
  logoSrc,
  onLogoError,
  productOptions,
  selectedProduct,
  onSelectProduct,
  compact = false,
  mobile = false,
  showAdminCenterRow,
  onOpenAdminCenter,
}: SideMenuAccountCardProps) {
  const dispatch = useAppDispatch();
  const { logout } = useAuth0();

  const userProfile = useAppSelector((s) => s.userProfile);
  const userName = `${userProfile.firstName ?? ""} ${
    userProfile.lastName ?? ""
  }`.trim();
  const userEmail = userProfile.email;
  /* "Partner · CCC.123" (CTX-335), replacing the bare tenant display name: the
     name alone did not say what the user *is* in it, and the id is what support
     asks for. Null for an unrecognised tenant type — see the helper. */
  const accountScopeLine = getAccountScopeLine({
    tenantType: userProfile.TenantType,
    tenantCode: userProfile.Tenant?.customerId,
  });

  const [cardOpen, setCardOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  /* Covers both shells: the desktop panel is unmounted while closed, but the
     mobile sheet stays mounted and only toggles, so the reveal keys off the open
     flag rather than off mounting. */
  const revealingProducts = usePanelReveal(cardOpen);

  const closeCard = () => setCardOpen(false);

  // Close on outside click.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      // On mobile the content is a bottom sheet (portaled outside rootRef); its
      // own backdrop handles closing, so skip the window outside-click there.
      if (mobile || !cardOpen) return;
      if (rootRef.current?.contains(e.target as Node)) return;
      closeCard();
    };
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    return () =>
      window.removeEventListener(
        "pointerdown",
        onPointerDown as EventListener,
        { capture: true } as EventListenerOptions
      );
  }, [cardOpen, mobile]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCard();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = () => {
    dispatch(clearSelection());
    persistStorage.removeItem("persist:root");
    dispatch(clearNetworkEventsActiveCount());
    logout({ logoutParams: { returnTo: getLogoutRedirectUri() } });
  };

  /* Panel rows follow the same scale as the trigger and the nav — otherwise
     opening the card jumps back to the pre-CTX-242 14px. */
  const iconClass = `${ROW_ICON} text-semantic-text-secondary`;
  const rowClass = `flex w-full items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} text-left ${ROW_TEXT} text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active hover:text-semantic-text-brand`;

  /* Extracted so the reveal can render the very same list underneath its
     spinner — that is what reserves the exact height (see PanelRevealOverlay). */
  const productList = (
    <div className="px-2 py-2" role="radiogroup" aria-label="Product">
      {productOptions.map((p) => {
        const active = p === selectedProduct;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              onSelectProduct(p);
              closeCard();
            }}
            className={`flex w-full items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} text-left ${ROW_TEXT} ${TRANSITION_COLORS} ${FOCUS_RING} ${
              active
                ? "font-semibold text-semantic-text-brand"
                : "text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
            }`}
          >
            {/* `bg-current` ties the dot to the row's own ink, so it is the same
                teal as the label beside it in both themes rather than a second
                hardcoded brand colour that could drift from it. */}
            <span
              className={`flex ${ROW_ICON} shrink-0 items-center justify-center`}
              aria-hidden
            >
              <span
                className={
                  active
                    ? "h-1.5 w-1.5 rounded-full bg-current"
                    : "h-1.5 w-1.5 rounded-full ring-1 ring-semantic-border-secondary"
                }
              />
            </span>
            <span className="truncate">{p}</span>
          </button>
        );
      })}
    </div>
  );

  const cardBody = (
    <>
      {/* Product list. A radio dot (CTX-335), not the target glyph this used:
          the glyph rendered on every row and only changed colour, so "which
          product am I in" was carried by a hue difference alone. A filled dot
          against an empty ring reads as chosen/not-chosen by shape as well, which
          also survives the accent being unavailable to a colour-blind reader. */}
      {revealingProducts ? (
        <PanelRevealOverlay>{productList}</PanelRevealOverlay>
      ) : (
        productList
      )}

      {/* Identity */}
      <div className="border-t border-semantic-border-secondary" />
      <div className="flex items-start gap-2 px-4 pt-3">
        {/* `mt-0.5` centres the glyph on the name's cap height — the block below
            it is three lines, so aligning to the box would sit it too high. */}
        <ProfileIcon className={`mt-0.5 ${ROW_ICON} shrink-0 text-semantic-text-secondary`} />
        <div className="min-w-0">
          <p className={`truncate ${ROW_TEXT} font-semibold text-semantic-text-primary`}>
            {userName}
          </p>
          {/* `truncate` needs the `min-w-0` on the wrapper above to engage — a
              long address is cut with an ellipsis rather than widening the panel
              past the rail (CTX-335 AC). */}
          {userEmail && (
            <p
              className="truncate text-[10px] text-semantic-text-secondary"
              title={userEmail}
            >
              {userEmail}
            </p>
          )}
          {accountScopeLine && (
            <p className="truncate text-[10px] text-semantic-text-secondary">
              {accountScopeLine}
            </p>
          )}
        </div>
      </div>

      {/* Display (theme) */}
      <div className="flex items-center justify-between px-4 py-2">
        <span className={`flex items-center ${ROW_GAP} ${ROW_TEXT} text-semantic-text-secondary`}>
          {/* The `-ml-[3px]` pull existed to cancel the whitespace a 20px render
              of this asset carried; at the row size it lines up on its own. */}
          <IconDisplay className={`${ROW_ICON} shrink-0 [&_path]:fill-current`} />
          Display
        </span>
        <DisplayToggle />
      </div>

      {/* Admin center + Log out. The divider is what makes this read as its own group
          rather than as two more rows of the Display block above it — the panel is
          grouped by hairlines throughout (CTX-335). */}
      <div className="border-t border-semantic-border-secondary" />
      <div className="px-2 py-2">
        {/* "Admin center", not "Settings" (CTX-583): everything a non-admin
            used to come here for is a rail row now, so the label finally
            describes what the panel behind it actually holds. */}
        {showAdminCenterRow && (
          <button
            type="button"
            className={rowClass}
            onClick={(e) => {
              onOpenAdminCenter(e);
              closeCard();
            }}
          >
            <IconSettings className={iconClass} />
            <span>Admin center</span>
          </button>
        )}
        <button type="button" className={rowClass} onClick={handleLogout}>
          <IconLogout className={iconClass} />
          <span>Log out</span>
        </button>
      </div>

      {/* Foot: small left-aligned wordmark over its tagline (CTX-335). The
          wordmark is held at 16px — half the trigger's — because down here it is
          a signature rather than a control; at the previous 24px it was the
          heaviest thing in the panel and pulled the eye past Log out. */}
      <div className="flex flex-col items-start gap-0.5 border-t border-semantic-border-secondary bg-semantic-bg-secondary px-4 py-2">
        <img
          src={logoSrc}
          alt="Brand logo"
          className="max-h-4 w-auto object-contain text-semantic-text-primary"
          onError={onLogoError}
        />
        <span className="text-[10px] leading-none text-semantic-text-secondary">
          Cloud Transit Xchange
        </span>
      </div>
    </>
  );

  return (
    /* `min-w-0` so the trigger inside can actually shrink: this element is itself
       a flex item of the chrome row, and without it the browser floors it at the
       product name's intrinsic width and the truncation above never runs.
       `flex-1` only on the wide variants — the collapsed rail wants the intrinsic
       32px square, not a stretched one. */
    <div
      ref={rootRef}
      className={compact ? "relative" : "relative min-w-0 flex-1"}
    >
      {/* Trigger: logo + selected product + chevron. Borderless on purpose
          (CTX-335): the button's fill already resolved to the rail's own
          `bg-semantic-bg-primary`, so the 1px border was the only thing making
          it read as a pill floating *on* the rail instead of as the rail's own
          heading. Both are dropped, leaving hover as the sole affordance.

          Deliberately NOT applied to its neighbours: the collapse/expand toggle
          in `SideMenuTopChrome` keeps its border, and so does the dropdown panel
          below. This is the product switcher only — do not "unify" the three. */}
      <button
        type="button"
        onClick={() => setCardOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={cardOpen}
        aria-label="Account menu"
        /* `w-full min-w-0` on every wide variant, not just mobile: on desktop the
           trigger sat in a flex row beside the collapse toggle with no width
           constraint, so a long product name pushed the toggle off the rail
           instead of engaging the label's `truncate` (CTX-335 AC). A flex item
           will not shrink below its content unless `min-w-0` says it may. */
        className={
          compact
            ? `flex h-8 w-8 items-center justify-center rounded-md ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`
            : `flex ${CHROME_TRIGGER} w-full min-w-0 items-center gap-2 rounded-md px-2 ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`
        }
      >
        {compact ? (
          <img
            src={logoSrc}
            alt="Brand logo"
            className="max-h-5 max-w-full object-contain text-semantic-text-primary"
            onError={onLogoError}
          />
        ) : (
          <>
            <span
              className={`flex ${CHROME_LOGO} shrink-0 items-center justify-center overflow-hidden rounded bg-semantic-bg-secondary`}
            >
              <img
                src={logoSrc}
                alt="Brand logo"
                className="max-h-full max-w-full object-contain text-semantic-text-primary"
                onError={onLogoError}
              />
            </span>
            <span
              className={`min-w-0 flex-1 truncate text-left ${ROW_TEXT} font-normal text-semantic-text-primary`}
              title={selectedProduct}
            >
              {selectedProduct}
            </span>
            <ChevronDownSvg
              className={`${CHEVRON_ICON} shrink-0 text-semantic-text-secondary ${TRANSITION_TRANSFORM} ${
                cardOpen ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      {mobile ? (
        <MobileBottomSheet open={cardOpen} onClose={closeCard}>
          {cardBody}
        </MobileBottomSheet>
      ) : (
        cardOpen && (
          /* Bound to the rail width so the panel always sits flush with it. It was
             a fixed `w-72` (288px), which fit inside the old 304px rail but would
             overhang the narrowed one and read as a misalignment (CTX-242). */
          <div
            className={`absolute left-0 top-full z-[60] mt-2 ${RAIL_WIDTH_EXPANDED} overflow-hidden rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary shadow-xl ${PANEL_OPEN_DOWN}`}
          >
            {cardBody}
          </div>
        )
      )}
    </div>
  );
}
